import type { AxisName, ButtonAction } from './actions';

/**
 * Touch input logic, kept free of the DOM so it is unit-tested (touch.test.ts): the floating analog
 * stick's maths, which finger owns which control (multi-touch), and how the stick and the pedals map
 * onto the game's axes. The on-screen layer (src/ui/touchControls.ts) feeds pointer events in and hands
 * the result to `InputManager.setTouchButton / setTouchAxes` — gameplay still only reads actions/axes.
 */

export interface StickConfig {
  /** Knob travel in CSS px for full deflection. */
  radius: number;
  /** Radial dead zone as a fraction of the travel (0..0.95): inside reads 0, the rest is rescaled to 0..1. */
  deadZone: number;
  /** Floating stick: when the finger leaves the ring the base is dragged along behind it. */
  follow: boolean;
}

export interface StickVector {
  /** Right (+) / left (-), -1..1. */
  x: number;
  /** Up the screen (+) / down (-), -1..1. */
  y: number;
  /** 0..1 after the dead zone. */
  mag: number;
}

export interface Point {
  x: number;
  y: number;
}

export const DEFAULT_STICK: StickConfig = { radius: 56, deadZone: 0.14, follow: true };

/**
 * Finger offset from the stick centre in screen px (+y is DOWN the screen) to a stick vector (+y is UP),
 * clamped to the unit disc, with a radial dead zone whose remainder is rescaled so the output still
 * reaches 1 at the rim and grows smoothly from 0 just outside the dead zone.
 */
export function stickVector(dx: number, dy: number, cfg: Pick<StickConfig, 'radius' | 'deadZone'>): StickVector {
  const r = Math.max(1, cfg.radius);
  let x = dx / r;
  let y = -dy / r;
  let m = Math.hypot(x, y);
  if (m > 1) {
    x /= m;
    y /= m;
    m = 1;
  }
  const dz = Math.max(0, Math.min(0.95, cfg.deadZone));
  if (m <= dz || m === 0) return { x: 0, y: 0, mag: 0 };
  const scaled = (m - dz) / (1 - dz);
  return { x: (x / m) * scaled, y: (y / m) * scaled, mag: scaled };
}

/** Floating base: once the finger is further than `radius` from the base, pull the base after it. */
export function followBase(base: Point, finger: Point, radius: number): Point {
  const dx = finger.x - base.x;
  const dy = finger.y - base.y;
  const d = Math.hypot(dx, dy);
  if (d <= radius || d === 0) return { x: base.x, y: base.y };
  const k = (d - radius) / d;
  return { x: base.x + dx * k, y: base.y + dy * k };
}

/** Stick low-pass rate (per second) at the centre and at the rim (StickFilter). Never under ~25/s: a fight must not feel laggy. */
export const STICK_FILTER_RATE_MIN = 25;
export const STICK_FILTER_RATE_MAX = 32;

/**
 * Low-pass filter on the on-foot stick, fed every frame by touchControls. A thumb wobbles by a pixel
 * or so; near the centre that is several degrees of direction, and the body used to follow every
 * wobble. The vector is smoothed with a rate that grows a little with the deflection: a 1 px wobble at
 * a quarter push moves the heading well under a degree (the damped body turn in footShaping.ts takes
 * the rest), and a push to the rim has its direction from the first frame and 90 % of its magnitude
 * within ~70 ms. Letting go reads 0 at once — stopping is the player's own deceleration, not the
 * filter's. Pure (touch.test.ts).
 */
export class StickFilter {
  private x = 0;
  private y = 0;

  step(raw: StickVector, dt: number): StickVector {
    if (!(raw.mag > 0)) {
      this.reset();
      return { x: 0, y: 0, mag: 0 };
    }
    const rate = STICK_FILTER_RATE_MIN + (STICK_FILTER_RATE_MAX - STICK_FILTER_RATE_MIN) * Math.min(1, raw.mag);
    const k = 1 - Math.exp(-rate * Math.max(0, dt));
    this.x += (raw.x - this.x) * k;
    this.y += (raw.y - this.y) * k;
    return { x: this.x, y: this.y, mag: Math.min(1, Math.hypot(this.x, this.y)) };
  }

  reset(): void {
    this.x = 0;
    this.y = 0;
  }
}

/** What a finger landed on. Pedals are axis buttons (accelerate +1 / brake-reverse -1). */
export type TouchControl = { kind: 'stick' } | { kind: 'button'; action: ButtonAction } | { kind: 'pedal'; value: 1 | -1 };

/**
 * Multi-touch bookkeeping. Each pointer id owns the control it first touched until it lifts (sliding
 * off a button does not drop it, so a thumb rolling over the edge keeps running / braking). Only one
 * finger drives the stick; another finger landing in the stick zone is ignored. A button stays held
 * while any finger holds it.
 */
export class TouchTracker {
  private readonly owners = new Map<number, TouchControl>();
  private stickId: number | null = null;
  private base: Point = { x: 0, y: 0 };
  private finger: Point = { x: 0, y: 0 };

  constructor(public cfg: StickConfig = { ...DEFAULT_STICK }) {}

  /** A finger came down on `control` at (x, y) px. Returns false when the touch is ignored. */
  down(id: number, control: TouchControl, x: number, y: number): boolean {
    if (this.owners.has(id)) this.up(id);
    if (control.kind === 'stick') {
      if (this.stickId !== null) return false;
      this.stickId = id;
      this.base = { x, y };
      this.finger = { x, y };
    }
    this.owners.set(id, control);
    return true;
  }

  move(id: number, x: number, y: number): void {
    if (id !== this.stickId) return;
    this.finger = { x, y };
    if (this.cfg.follow) this.base = followBase(this.base, this.finger, this.cfg.radius);
  }

  /** The finger lifted (or was cancelled). Returns what it was holding. */
  up(id: number): TouchControl | null {
    const c = this.owners.get(id) ?? null;
    this.owners.delete(id);
    if (id === this.stickId) this.stickId = null;
    return c;
  }

  releaseAll(): void {
    this.owners.clear();
    this.stickId = null;
  }

  owns(id: number): boolean {
    return this.owners.has(id);
  }

  get fingers(): number {
    return this.owners.size;
  }

  get stickActive(): boolean {
    return this.stickId !== null;
  }

  /** Where to draw the base and the knob (px), while the stick is held. */
  get stickBase(): Point {
    return { ...this.base };
  }

  get stickKnob(): Point {
    const dx = this.finger.x - this.base.x;
    const dy = this.finger.y - this.base.y;
    const d = Math.hypot(dx, dy);
    const r = this.cfg.radius;
    if (d <= r) return { ...this.finger };
    return { x: this.base.x + (dx / d) * r, y: this.base.y + (dy / d) * r };
  }

  get stick(): StickVector {
    if (this.stickId === null) return { x: 0, y: 0, mag: 0 };
    return stickVector(this.finger.x - this.base.x, this.finger.y - this.base.y, this.cfg);
  }

  isHeld(a: ButtonAction): boolean {
    for (const c of this.owners.values()) if (c.kind === 'button' && c.action === a) return true;
    return false;
  }

  heldButtons(): Set<ButtonAction> {
    const out = new Set<ButtonAction>();
    for (const c of this.owners.values()) if (c.kind === 'button') out.add(c.action);
    return out;
  }

  /** Pedal axis: +1 accelerate, -1 brake/reverse, 0 none. Braking wins when both are pressed. */
  pedal(): number {
    let gas = false;
    let brake = false;
    for (const c of this.owners.values()) {
      if (c.kind !== 'pedal') continue;
      if (c.value > 0) gas = true;
      else brake = true;
    }
    return brake ? -1 : gas ? 1 : 0;
  }

  pedalHeld(): boolean {
    for (const c of this.owners.values()) if (c.kind === 'pedal') return true;
    return false;
  }
}

/** A press on a hold-or-latch knob shorter than this (ms) is a tap: it latches. */
export const LATCH_TAP_MS = 220;

/**
 * A knob that works held AND as a toggle (touch AIM): held, it is on while the finger is down, as
 * before; a quick tap latches it on (the thumb is then free for FIRE), and the next tap lets it go.
 * Pure; the caller holds the action while `on` is true.
 */
export class HoldOrLatch {
  private latchedOn = false;
  private down_ = false;
  private downAt = 0;
  private unlatching = false;

  /** A finger came down on the knob at `now` (ms). */
  down(now: number): void {
    this.down_ = true;
    this.downAt = now;
    this.unlatching = this.latchedOn;
    this.latchedOn = false;
  }

  /** The finger lifted at `now` (ms). Returns whether the action stays on (it latched). */
  up(now: number): boolean {
    if (!this.down_) return this.latchedOn;
    this.down_ = false;
    if (this.unlatching) {
      this.unlatching = false;
      return false;
    }
    this.latchedOn = now - this.downAt <= LATCH_TAP_MS;
    return this.latchedOn;
  }

  /** Let go for good (layer hidden, into a car, weapons put away). */
  reset(): void {
    this.latchedOn = false;
    this.down_ = false;
    this.unlatching = false;
  }

  get latched(): boolean {
    return this.latchedOn;
  }

  /** The action is on: the finger is down or it is latched. */
  get on(): boolean {
    return this.down_ || this.latchedOn;
  }
}

/**
 * Touch RUN: held, or tapped to latch like AIM, with one more rule — the latch is for the push in
 * progress, so the stick thumb lifting lets it go (a tapped RUN never carries into the next push, and
 * nobody walks into a menu at a sprint). Held RUN is untouched by the stick. Pure (touch.test.ts).
 */
export class RunLatch extends HoldOrLatch {
  /** The stick thumb lifted. Returns whether RUN is still on (only while a finger holds it). */
  stickReleased(): boolean {
    if (this.latched) this.reset();
    return this.on;
  }
}

export type TouchContext = 'foot' | 'car' | 'none';

export interface AxisMapOptions {
  /** In a car with no pedal pressed, pushing the stick up/down also accelerates/brakes. */
  stickThrottle: boolean;
  /** Steering response exponent (>1 = finer control near the centre). */
  steerCurve: number;
}

// 2026-10-11 owner: the stick only steers; GAS / BRAKE own the throttle and reverse (stick throttle was confusing).
export const DEFAULT_AXIS_MAP: AxisMapOptions = { stickThrottle: false, steerCurve: 1.35 };

/**
 * Stick + pedals -> the game's axes. On foot the stick is the screen-relative move vector; in a car its
 * x steers and the pedals own the throttle (the stick's y only when no pedal is held).
 */
export function mapTouchAxes(ctx: TouchContext, stick: StickVector, pedal: number, pedalHeld: boolean, o: AxisMapOptions = DEFAULT_AXIS_MAP): Record<AxisName, number> {
  const out: Record<AxisName, number> = { moveX: 0, moveY: 0, throttle: 0, steer: 0 };
  if (ctx === 'foot') {
    out.moveX = stick.x;
    out.moveY = stick.y;
  } else if (ctx === 'car') {
    out.steer = Math.sign(stick.x) * Math.pow(Math.abs(stick.x), Math.max(1, o.steerCurve));
    out.throttle = pedalHeld ? pedal : o.stickThrottle ? stick.y : 0;
  }
  return out;
}

// ----------------------------------------------------------------------------- touch UI detection

/**
 * Whether this device should get the touch layer: a coarse primary pointer (phones, tablets), or a
 * touch screen without any hover-capable pointer. `?touch` / `?notouch` force it either way.
 */
export function prefersTouchUi(win: Pick<Window, 'matchMedia' | 'location' | 'navigator'> | null = typeof window !== 'undefined' ? window : null): boolean {
  if (!win) return false;
  const q = new URLSearchParams(win.location?.search ?? '');
  if (q.has('notouch')) return false;
  if (q.has('touch')) return true;
  const mm = (s: string): boolean => (typeof win.matchMedia === 'function' ? win.matchMedia(s).matches : false);
  if (mm('(pointer: coarse)')) return true;
  return (win.navigator?.maxTouchPoints ?? 0) > 0 && mm('(any-pointer: coarse)') && !mm('(any-hover: hover)');
}

type Listener = (on: boolean) => void;
let touchUiOn = false;
const listeners = new Set<Listener>();

/**
 * Process-wide "touch UI is on" switch (set by src/ui/mobile.ts at boot, or on the first real touch).
 * UI modules read it to show touch affordances; the `bh-touch` class on <html> mirrors it for CSS.
 */
export const touchUi = {
  get enabled(): boolean {
    return touchUiOn;
  },
  set(on: boolean): void {
    if (on === touchUiOn) return;
    touchUiOn = on;
    for (const l of [...listeners]) l(on);
  },
  onChange(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
